import { addSeconds } from 'date-fns';

import type { PlanAppointmentsRequest, ViewportContext } from 'generated';
import type { Event, PlanningEntityMap } from '../types';
import type { Interval } from '../TimelineView/types';
import type { ZoomLevel } from '../TimelineView/TimelineBar/TimelineBar.enums';
import { sortByDate } from '../utils';
import { getSecondsPerPixel } from '../TimelineView/utils';

export const TIMELINE_DRAGGABLE_SCROLL_CONTAINER_CLASS =
  '__TIMELINE_DRAGGABLE_SCROLL_CONTAINER_CLASS_PRAGMATIC__';

/** Find the dragged occurrence inside the active entity's schedules / to-plan tasks. */
export const resolveDraggedEvent = (
  planningEntities: PlanningEntityMap,
  planningEntityId: string,
  id: string,
  isToPlan: boolean
): Event | null =>
  (!isToPlan
    ? planningEntities?.[planningEntityId]?.schedules
        .map((s) => s.events)
        .flat()
        .find((event): event is Event => event?.id === id)
    : planningEntities?.[planningEntityId]?.planningTasks
        ?.map((task) => task.data)
        .find((event): event is Event => (event as Event)?.id === id)) ?? null;

/**
 * Chip preview is centered on the cursor, so its left edge sits at
 * `cursorX - chipWidth/2`, translated into the route element's local coords.
 */
export const getChipLeftInRoute = (
  input: { clientX: number },
  overElement: HTMLElement | undefined,
  chipWidthPx: number
): number => {
  const routeRect = overElement?.getBoundingClientRect();
  const cursorXInRoute = routeRect ? input.clientX - routeRect.left : 0;
  return cursorXInRoute - chipWidthPx / 2;
};

/** Translate a route-local x offset into a drop time on the timeline. */
export const getDropDate = (
  chipLeftInRoute: number,
  interval: Interval,
  timeBarItemWidth: number,
  zoomLevel: ZoomLevel
): Date =>
  addSeconds(
    interval.start,
    chipLeftInRoute * getSecondsPerPixel(timeBarItemWidth, zoomLevel)
  );

/** Drop start (seconds/ms zeroed), or null when dropping onto the to-plan zone. */
export const computeDropStart = ({
  chipLeftInRoute,
  interval,
  timeBarItemWidth,
  zoomLevel,
  overIsToPlan
}: {
  chipLeftInRoute: number;
  interval: Interval;
  timeBarItemWidth: number;
  zoomLevel: ZoomLevel;
  overIsToPlan: boolean;
}): Date | null => {
  if (overIsToPlan) return null;
  const droppedTime = getDropDate(
    chipLeftInRoute,
    interval,
    timeBarItemWidth,
    zoomLevel
  );
  droppedTime.setMilliseconds(0);
  droppedTime.setSeconds(0);
  return droppedTime;
};

/**
 * Build the plan-appointments request for a (possibly multi-select) drop.
 * `overData`/`start` are `any` to mirror the original TimelineView: the backend
 * accepts a Date for `start` even though the type is `string | undefined`.
 */
export const buildDropPayload = ({
  activeEvent,
  selectedEvents,
  overData,
  start,
  sourceViewport
}: {
  activeEvent: Event;
  selectedEvents: Event[];
  overData: any;
  start: any;
  /** The row being dragged out of; completed with the board's dates and rows in handlePlanAppointments. */
  sourceViewport?: Pick<ViewportContext, 'sourcePlanningEntityIdentifiers'>;
}): PlanAppointmentsRequest => ({
  appointmentOccurrences:
    selectedEvents.length === 0
      ? [
          {
            entityId: activeEvent.id,
            originalOccurrenceStart: activeEvent.start,
            schedulingLabel: activeEvent.schedulingLabel
          }
        ]
      : sortByDate(
          selectedEvents.map((event: Event) => ({
            entityId: event.id,
            originalOccurrenceStart: event.start,
            schedulingLabel: event.schedulingLabel
          })),
          'originalOccurrenceStart'
        ),
  planningEntity: {
    ...overData.planningEntityIdentifier,
    start: overData.scheduleStart
  },
  editAppointmentType: 'ONLY_THIS',
  suppressWarnings: false,
  start,
  viewport: sourceViewport
});
